/**
 * Pure bucket-grid time math for the uptime bars. UTC / clock-skew /
 * ingest-lag sensitive — extracted from `App.tsx` so it can be unit-tested
 * and shared between `UptimeBlock` and `StatusHistoryBar` without drift.
 */
import type { UptimeWindow, WindowBucket } from './status-types';

export function bucketMsForWindow(w: UptimeWindow): number {
  if (w === '1h') return 60_000;
  if (w === '24h') return 15 * 60_000;
  return 86_400_000;
}

/**
 * "Pending" = bucket sits inside a slice where the probe is expected to run
 * but the result hasn't propagated yet. Renders as a shimmering placeholder
 * instead of a flat grey cell so the operator can distinguish "data is on
 * the way" from "we missed this minute". Window grace = 2 bucket-widths past
 * expected end (covers ingest lag + clock skew).
 */
export function isPendingBucket(
  startAt: string | null | undefined,
  totalProbes: number,
  windowMs: number,
  now: number,
): boolean {
  if (totalProbes > 0) return false;
  if (!startAt) return false;
  const start = new Date(startAt).getTime();
  if (!Number.isFinite(start)) return false;
  const expectedEnd = start + windowMs;
  return now - expectedEnd < 2 * windowMs;
}

/**
 * Backend returns buckets up to the latest completed slot — the current and
 * just-passed slots are missing while ingest catches up. Append synthetic
 * pending placeholders so the timeline extends to "now" and the shimmer
 * effect signals "data on the way". Capped at 3 to avoid runaway placeholder
 * tails when the backend is hours behind.
 */
export function padTrailingPending(
  buckets: readonly WindowBucket[],
  windowMs: number,
  now: number,
): WindowBucket[] {
  if (buckets.length === 0) return [...buckets];
  const last = buckets[buckets.length - 1];
  if (!last.startAt || !last.endAt) return [...buckets];
  const lastEnd = new Date(last.endAt).getTime();
  if (!Number.isFinite(lastEnd) || now <= lastEnd) return [...buckets];
  // floor + 1 covers both the just-completed slot AND the currently-filling slot.
  const slotsBehind = Math.floor((now - lastEnd) / windowMs) + 1;
  const toAppend = Math.min(Math.max(slotsBehind, 0), 3);
  if (toAppend === 0) return [...buckets];
  const out: WindowBucket[] = [...buckets];
  let slotStart = lastEnd;
  for (let i = 0; i < toAppend; i += 1) {
    const slotEnd = slotStart + windowMs;
    out.push({
      startAt: new Date(slotStart).toISOString(),
      endAt: new Date(slotEnd).toISOString(),
      status: 'no_data',
      okCount: 0,
      warnCount: 0,
      downCount: 0,
      totalProbes: 0,
      successPct: 0,
    });
    slotStart = slotEnd;
  }
  return out;
}

/**
 * Anchored (historical) windows end well before now — extending the timeline
 * toward the real clock tacks phantom no-data cells onto the end. In that case
 * pad only up to the last real bucket; for live windows pad to `now` so the
 * current slot still shimmers. The signal is the last real bucket's end: the
 * backend fills the grid to `endAt`, so a live (even ingest-behind) window has
 * a recent last bucket, while an anchored window's is old.
 */
export function trailingPadNow(
  lastEndIso: string | null | undefined,
  windowMs: number,
  renderNow: number,
): number {
  const lastEnd = lastEndIso ? new Date(lastEndIso).getTime() : Number.NaN;
  if (Number.isFinite(lastEnd) && renderNow - lastEnd > 2 * windowMs) {
    return lastEnd;
  }
  return renderNow;
}
